import { Bell, Compass, Mail, Plus, Home, Search, Settings } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { NavLink, useLocation } from 'react-router-dom'

import { Avatar } from '@/components/ui/Avatar'
import { useCurrentUser } from '@/features/auth'
import { useDmUnreadCount } from '@/features/messages'
import { unreadLabel, useUnreadCount } from '@/features/notifications'
import { useT } from '@/i18n'
import type { MessageKey } from '@/i18n'
import { cn } from '@/lib/cn'
import { useUiStore } from '@/stores/ui'

import { AccountMenu } from './AccountMenu'

interface NavItem {
  to: string
  label: MessageKey
  icon: LucideIcon
  end: boolean
  badge?: 'notifications' | 'messages'
  /** 只给登录的人看 */
  auth?: boolean
}

const items: NavItem[] = [
  { to: '/', label: 'nav.home', icon: Home, end: true },
  { to: '/explore', label: 'nav.explore', icon: Compass, end: false },
  { to: '/search', label: 'nav.search', icon: Search, end: false },
  {
    to: '/notifications',
    label: 'nav.notifications',
    icon: Bell,
    end: false,
    badge: 'notifications',
  },
  { to: '/messages', label: 'nav.messages', icon: Mail, end: false, badge: 'messages', auth: true },
  // 手机上没有侧栏，设置只能从这里进
  { to: '/settings', label: 'nav.settings', icon: Settings, end: false },
]

/**
 * < 640px 时的底部 tab 栏，发帖是浮动按钮。
 *
 * 最右侧的头像是**窄屏下唯一**的账号入口：这个断点下整个侧栏都不渲染，
 * 没有它的话个人资料、书签、设置、退出登录一个都够不到。
 */
export function BottomNav() {
  const { user } = useCurrentUser()
  const openComposer = useUiStore((state) => state.openComposer)
  const unread = useUnreadCount(Boolean(user))
  const dmUnread = useDmUnreadCount(Boolean(user))
  const counts = {
    notifications: {
      count: unread.data?.count ?? 0,
      truncated: Boolean(unread.data?.truncated),
    },
    messages: { count: dmUnread.data?.count ?? 0, truncated: Boolean(dmUnread.data?.truncated) },
  }
  const t = useT()
  // 对话页底部是输入框，浮动的发帖按钮会正好压在「发送」上
  const inConversation = useLocation().pathname.startsWith('/messages')

  return (
    <>
      {user && !inConversation && (
        <button
          type="button"
          onClick={() => openComposer()}
          aria-label={t('common.post')}
          className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 z-30 flex size-14 items-center justify-center px-corners bg-brand text-on-brand shadow-lg sm:hidden"
        >
          <Plus className="size-6" />
        </button>
      )}

      <nav
        className="pb-safe fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-surface sm:hidden"
        aria-label={t('nav.bottom')}
      >
        {items
          .filter((item) => !item.auth || user)
          .map((item) => {
            const badge = item.badge ? counts[item.badge] : null
            const unreadCount = badge?.count ?? 0
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex flex-1 items-center justify-center py-3',
                    isActive ? 'text-ink' : 'text-ink-muted',
                  )
                }
                aria-label={
                  badge && unreadCount > 0
                    ? t(item.label) +
                      t('nav.unreadSuffix', { count: unreadLabel(unreadCount, badge.truncated) })
                    : t(item.label)
                }
              >
                <span className="relative">
                  <item.icon className="size-6" />
                  {badge && unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 size-2 bg-brand-ink" aria-hidden />
                  )}
                </span>
              </NavLink>
            )
          })}

        {user ? (
          <div className="flex flex-1 items-center justify-center">
            <AccountMenu
              align="right"
              includeNavItems
              trigger={({ toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  aria-label={t('account.menuLabel', { username: user.username })}
                  className="flex items-center justify-center p-1"
                >
                  <Avatar src={user.avatar_url} alt={user.display_name} size="xs" />
                </button>
              )}
            />
          </div>
        ) : (
          <NavLink
            to="/login"
            className="flex flex-1 items-center justify-center py-3 text-[15px] font-bold text-brand-ink"
          >
            {t('common.login')}
          </NavLink>
        )}
      </nav>
    </>
  )
}
